import { useAdminResearchResults } from '../../../hooks/admin/useAdminQueries'
import { useAdminStore, ITEMS_PER_PAGE } from '../../../stores/adminStore'
import ResearchResultsTable from './ResearchResultsTable'
import React, { useState, useEffect } from 'react'

// TODO: Import a toast library for copy feedback if available

const ResearchResultsPanel: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const [selectedResultIds, setSelectedResultIds] = useState<Set<string>>(new Set())
    const [copySuccess, setCopySuccess] = useState<boolean>(false)

    const selectedUserId = useAdminStore((state) => state.selectedUserId)

    const {
        data: resultsData,
        isLoading,
        isError,
        error,
        isFetching,
    } = useAdminResearchResults(selectedUserId, {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
    })

    // Reset page and selection when user changes
    useEffect(() => {
        setCurrentPage(1)
        setSelectedResultIds(new Set())
    }, [selectedUserId])

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= (resultsData?.totalPages || 1) && !isFetching) {
            setCurrentPage(newPage)
            setSelectedResultIds(new Set()) // Clear selection on page change
        }
    }

    const handleToggleSelection = (resultId: string) => {
        setSelectedResultIds((prev: Set<string>) => {
            const newSelection = new Set(prev)
            if (newSelection.has(resultId)) {
                newSelection.delete(resultId)
            } else {
                newSelection.add(resultId)
            }
            return newSelection
        })
    }

    const handleSelectAll = (allVisibleIds: string[]) => {
        const allSelected = allVisibleIds.every((id) => selectedResultIds.has(id))

        if (allSelected) {
            // Deselect all visible
            setSelectedResultIds((prev: Set<string>) => {
                const newSelection = new Set(prev)
                allVisibleIds.forEach((id) => newSelection.delete(id))
                return newSelection
            })
        } else {
            // Select all visible
            setSelectedResultIds((prev: Set<string>) => {
                const newSelection = new Set(prev)
                allVisibleIds.forEach((id) => newSelection.add(id))
                return newSelection
            })
        }
    }

    const handleCopySelected = async () => {
        if (selectedResultIds.size === 0 || !resultsData?.data) return

        const selectedItems = resultsData.data.filter((item) => selectedResultIds.has(item.id))

        if (selectedItems.length === 0) return

        const textToCopy = selectedItems
            .map((item) => `Title: ${item.title}\nURL: ${item.url}\nSummary: ${item.summary}`)
            .join('\n\n---\n\n')

        try {
            await navigator.clipboard.writeText(textToCopy)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
            console.log('Copied research results to clipboard')
        } catch (err) {
            console.error('Failed to copy text: ', err)
            alert('Failed to copy results.')
        }
    }

    const allVisibleIds = resultsData?.data?.map((item) => item.id) || []

    return (
        <div className="flex-grow flex flex-col p-4 bg-gray-50 overflow-auto">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">
                Research Results {selectedUserId ? 'for Selected User' : ''}
            </h2>

            {!selectedUserId ? (
                <div className="text-center text-gray-500 py-10">
                    Select a user from the list to view their research results.
                </div>
            ) : isLoading ? (
                <div className="text-center text-gray-500 py-10">Loading results...</div>
            ) : isError ? (
                <div className="text-center text-red-500 py-10">
                    Error loading results: {error?.message || 'Unknown error'}
                </div>
            ) : (
                <>
                    <div className="mb-4 flex justify-between items-center">
                        <span className="text-sm text-gray-600">
                            Showing {resultsData?.data?.length || 0} of {resultsData?.total || 0}{' '}
                            results
                        </span>
                        <button
                            onClick={handleCopySelected}
                            disabled={selectedResultIds.size === 0}
                            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {copySuccess ? 'Copied!' : `Copy Selected (${selectedResultIds.size})`}
                        </button>
                    </div>
                    <ResearchResultsTable
                        results={resultsData?.data || []}
                        isLoading={isFetching}
                        selectedResultIds={selectedResultIds}
                        onToggleSelection={handleToggleSelection}
                        onSelectAll={handleSelectAll}
                        allVisibleIds={allVisibleIds}
                    />
                    {resultsData && resultsData.totalPages > 1 && (
                        <div className="mt-4 flex justify-between items-center">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1 || isFetching}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-700">
                                Page {currentPage} of {resultsData.totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === resultsData.totalPages || isFetching}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default ResearchResultsPanel
